import { ITradeShift, TradeShift } from "./trade-shift";
import { differenceInDays } from "date-fns";
import { IBreakValues } from "../break";
import { ExternalApplication } from "../external-applications";

export enum ShiftTypeEnum {
	Absence = 1,
	Schedule = 2,
	Work = 3,
	Application = 4,
	AbsenceRequest = 5,
}

export enum EmployeeShiftApplicationState {
	/**
	 * Ingen
	 */
	None = 0,
	/**
	 * Förfrågad till anställd
	 */
	Inquired = 1,
	/**
	 * Nekad av anställd
	 */
	Declined = 2,
	/**
	 * Sökt av den anställde
	 */
	Accepted = 3,
	/**
	 * Tilldelad av schemaläggare
	 */
	Assigned = 4,
	/**
	 * Avslaget av schemaläggare
	 */
	Rejected = 5,

	/**
	 * Sökt och tilldelad
	 */
	AssignedAccepted = 6,
}

export interface IScheduleShift {
	id: number;
	system: string;
	stationName: string;
	stationId: number;
	professionName: string;
	professionId: number;
	date: Date;
	businessDate?: Date;
	isMyShift: boolean;
	inTime?: Date;
	outTime?: Date;
	shiftType: ShiftTypeEnum;
	forSale: boolean;
	applicationState?: EmployeeShiftApplicationState;
	absenceName?: string;
	comment?: string;
	isAbsenceApproved: boolean;
	tradeShifts: ITradeShift[];
	wage?: number;
	tag?: string;
	tagColor?: string;
	manualBreaks?: IBreakValues[];
	originalBreaks?: IBreakValues[];
	locationName: string;
	locationId: number;
	externalApplicationId?: ExternalApplication;
	extraProfessionNumber?: number;
}

export class ScheduleShift {
	readonly id: number;
	readonly system: string;
	readonly stationName: string;
	readonly stationId: number;
	readonly professionName: string;
	readonly professionId: number;
	readonly date: Date;
	readonly businessDate?: Date;
	readonly isMyShift: boolean;
	readonly inTime?: Date;
	readonly outTime?: Date;
	readonly shiftType: ShiftTypeEnum;
	readonly forSale: boolean;
	readonly applicationState?: EmployeeShiftApplicationState;
	readonly absenceName?: string;
	readonly comment?: string;
	readonly isAbsenceApproved: boolean;
	tradeShifts: ITradeShift[];
	readonly wage?: number;
	readonly tag?: string;
	readonly tagColor?: string;
	readonly manualBreaks?: IBreakValues[];
	readonly originalBreaks?: IBreakValues[];
	readonly locationName: string;
	readonly locationId: number;
	readonly externalApplicationId?: ExternalApplication;
	readonly extraProfessionNumber?: number;

	constructor(values: IScheduleShift, systemName: string) {
		this.id = values.id;
		this.system = systemName;
		this.stationName = values.stationName;
		this.stationId = values.stationId;
		this.professionName = values.professionName;
		this.professionId = values.professionId;
		this.date = new Date(values.date);
		this.businessDate = values.businessDate ? new Date(values.businessDate) : undefined;
		this.isMyShift = values.isMyShift;
		this.inTime = values.inTime ? new Date(values.inTime) : undefined;
		this.outTime = values.outTime ? new Date(values.outTime) : undefined;
		this.shiftType = values.shiftType;
		this.forSale = values.forSale;
		this.applicationState = values.applicationState;
		this.absenceName = values.absenceName;
		this.comment = values.comment;
		this.isAbsenceApproved = values.isAbsenceApproved;
		this.tradeShifts =
			values.tradeShifts?.map((ts) => new TradeShift(ts, systemName, values.professionName)) ?? [];
		this.wage = values.wage;
		this.tag = values.tag;
		this.tagColor = values.tagColor;
		this.manualBreaks = values.manualBreaks;
		this.originalBreaks = values.originalBreaks;
		this.locationName = values.locationName;
		this.locationId = values.locationId;
		this.externalApplicationId = values.externalApplicationId;
		this.extraProfessionNumber = values.extraProfessionNumber;
	}

	getBusinessDate() {
		return this.businessDate || this.date;
	}

	getDayOffSet() {
		if (!this.businessDate) {
			return 0;
		}
		return differenceInDays(this.date, this.businessDate);
	}

	isApplicationShiftAssigned = () => {
		return (
			this.applicationState === EmployeeShiftApplicationState.Assigned ||
			this.applicationState === EmployeeShiftApplicationState.AssignedAccepted
		);
	};

	hasValidInTimeAndOutTime = () => {
		return !this.inTime || !this.outTime;
	};

	isAbsence = () => {
		return this.shiftType === ShiftTypeEnum.Absence || this.shiftType === ShiftTypeEnum.AbsenceRequest;
	};

	isForSaleAndMyShift = () => this.forSale && this.isMyShift;

	isScheduleShift = () =>
		this.shiftType === ShiftTypeEnum.Schedule ||
		this.isApplicationShiftAssigned() ||
		this.isForSaleAndMyShift();

	isApplicationShift = () =>
		this.shiftType === ShiftTypeEnum.Application &&
		!this.isApplicationShiftAssigned() &&
		!this.isForSaleAndMyShift();

	isVikExtraProfession = () => this.extraProfessionNumber !== undefined && this.extraProfessionNumber === -1;

	isSävaExtraProfession = () => this.extraProfessionNumber !== undefined && this.extraProfessionNumber === -2;

	isVikOrSäva = () => this.isVikExtraProfession() || this.isSävaExtraProfession();

	getExtraProfessionLabel(): string | undefined {
		if (this.isVikExtraProfession()) return "VIK";
		if (this.isSävaExtraProfession()) return "SÄVA";
		return undefined;
	}
}
